<template>
  <div class="ea-page">
    <header class="ea-header">
      <b-img
        v-if="header.logo_url"
        :src="header.logo_url"
        alt="Company logo"
        class="ea-logo"
      />
      <div class="ea-header-text">
        <router-link
          v-if="dashboardId"
          :to="{ name: 'external-supervisor' }"
          class="ea-back"
          data-test="back-to-list"
        >
          ← All my timesheets
        </router-link>
        <div v-if="header.company_name" class="ea-company">
          {{ header.company_name }}
        </div>
        <h1 class="ea-title">Timesheet approval</h1>
        <div v-if="header.project_name" class="ea-sub">
          {{ header.tradie_name }} · {{ header.project_name }} ·
          {{ weekLabel }}
        </div>
      </div>
    </header>

    <main class="ea-body">
      <!-- Loading -->
      <div v-if="step === 'loading'" class="ea-card text-center">
        <b-spinner small /> Loading…
      </div>

      <!-- Link no good: invalid, replaced, expired or cancelled -->
      <div v-else-if="step === 'blocked'" class="ea-card" data-test="blocked">
        <h2 class="ea-h2">{{ blockedTitle }}</h2>
        <p class="mb-0">{{ blockedMessage }}</p>
      </div>

      <!-- Step 1: ask for the emailed code -->
      <div
        v-else-if="step === 'send-code'"
        class="ea-card"
        data-test="send-code"
      >
        <h2 class="ea-h2">Confirm it's you</h2>
        <p>
          To protect
          {{ header.tradie_name ? `${header.tradie_name}'s` : 'this' }}
          timesheet, we email a 6-digit code to {{ link.code_sent_to }}.
        </p>
        <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
        <b-button
          variant="primary"
          block
          :disabled="busy"
          data-test="send-code-btn"
          @click="sendCode"
        >
          {{ busy ? 'Sending…' : 'Email me a code' }}
        </b-button>
      </div>

      <!-- Step 2: enter the code -->
      <div
        v-else-if="step === 'enter-code'"
        class="ea-card"
        data-test="enter-code"
      >
        <h2 class="ea-h2">Enter your code</h2>
        <p>
          We emailed a code to {{ codeSentTo }}. It can take a minute to arrive.
        </p>
        <b-form-input
          v-model="code"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="12"
          placeholder="6-digit code"
          class="ea-code-input"
          data-test="code-input"
          @keyup.enter="verifyCode"
        />
        <b-alert v-if="error" show variant="danger" class="mt-3 mb-0">
          {{ error }}
        </b-alert>
        <b-button
          variant="primary"
          block
          class="mt-3"
          :disabled="busy || !code.trim()"
          data-test="verify-btn"
          @click="verifyCode"
        >
          {{ busy ? 'Checking…' : 'Continue' }}
        </b-button>
        <b-button variant="link" block :disabled="busy" @click="sendCode">
          Send a new code
        </b-button>
      </div>

      <!-- Step 3: the timesheet -->
      <template v-else-if="step === 'sheet' && sheet">
        <b-alert
          v-if="isDecided"
          show
          :variant="decisionVariant"
          data-test="decided"
        >
          <strong>{{ decisionTitle }}</strong>
          <div v-if="sheet.decision.decided_at">
            {{ formatDateTime(sheet.decision.decided_at) }}
          </div>
          <div v-if="sheet.decision.rejection_reason">
            Reason: {{ sheet.decision.rejection_reason }}
          </div>
          <div v-if="sheet.decision.comment">
            Comment: {{ sheet.decision.comment }}
          </div>
          <div v-if="sheet.decision.confirmation_text" class="mt-1">
            “{{ sheet.decision.confirmation_text }}”
          </div>
          <router-link
            :to="{ name: 'external-supervisor' }"
            class="d-inline-block mt-2"
            data-test="other-timesheets"
          >
            {{
              dashboardId
                ? 'Back to all my timesheets'
                : 'View my other timesheets'
            }}
          </router-link>
        </b-alert>

        <section class="ea-card">
          <h2 class="ea-h2">Hours</h2>

          <div class="ea-days">
            <div class="ea-days-head" aria-hidden="true">
              <span>Day</span>
              <span>Start</span>
              <span>Finish</span>
              <span>Break (min)</span>
              <span class="text-right">Hours</span>
            </div>
            <div
              v-for="(day, i) in days"
              :key="day.date"
              class="ea-day"
              :class="{
                'ea-changed': dayIsChanged(i),
                'ea-invalid': dayErrors[i],
              }"
              :data-test="`day-${i}`"
            >
              <div class="ea-day-head">
                <strong>{{ formatDay(day.date) }}</strong>
                <span class="ea-worked" :data-test="`worked-${i}`">
                  {{
                    day.leave_type
                      ? leaveLabel(day.leave_type)
                      : formatMinutes(worked(day))
                  }}
                </span>
              </div>

              <div v-if="day.leave_type" class="ea-muted">
                Leave day, no hours to edit.
              </div>
              <div v-else class="ea-fields">
                <label>
                  <span>Start</span>
                  <b-form-input
                    v-model="day.start"
                    type="time"
                    :disabled="!canEdit"
                    :data-test="`start-${i}`"
                  />
                </label>
                <label>
                  <span>Finish</span>
                  <b-form-input
                    v-model="day.finish"
                    type="time"
                    :disabled="!canEdit"
                    :data-test="`finish-${i}`"
                  />
                </label>
                <label>
                  <span>Break (min)</span>
                  <b-form-input
                    v-model.number="day.break_minutes"
                    type="number"
                    min="0"
                    :disabled="!canEdit"
                    :data-test="`break-${i}`"
                  />
                </label>
              </div>

              <div v-if="dayErrors[i]" class="ea-error">{{ dayErrors[i] }}</div>
              <div
                v-else-if="dayIsChanged(i)"
                class="ea-was"
                :data-test="`was-${i}`"
              >
                {{ wasText(original[i]) }}
              </div>
              <div v-else-if="amendedByEarlierVersion(i)" class="ea-was">
                {{ wasText(day.original) }} before the supervisor changed it.
              </div>
            </div>
          </div>

          <div class="ea-total" data-test="total">
            <span>Total</span>
            <span>
              <strong>{{ formatMinutes(currentTotal) }}</strong>
              <span
                v-if="totalDifference"
                class="ea-diff"
                data-test="difference"
              >
                ({{ formatDifference(totalDifference) }} from
                {{ formatMinutes(originalTotal) }})
              </span>
            </span>
          </div>
        </section>

        <section v-if="plant.length" class="ea-card">
          <h2 class="ea-h2">Plant &amp; equipment</h2>
          <div
            v-for="(p, i) in plant"
            :key="`${p.date}-${p.plant_equipment_id}-${i}`"
            class="ea-plant"
            :class="{ 'ea-changed': plantIsChanged(i) }"
          >
            <div>
              <strong>{{ p.name || 'Equipment' }}</strong>
              <span v-if="p.plant_id_rego" class="ea-muted">
                · {{ p.plant_id_rego }}</span
              >
              <div class="ea-muted">
                {{ formatDay(p.date) }}
                <span v-if="plantIsChanged(i)">
                  · was {{ originalPlant[i].quantity }}
                </span>
              </div>
            </div>
            <b-form-input
              v-model.number="p.quantity"
              type="number"
              min="0"
              step="any"
              class="ea-qty"
              :disabled="!canEdit"
              :data-test="`plant-${i}`"
            />
          </div>
        </section>

        <section v-if="sheet.submitted.comment" class="ea-card">
          <h2 class="ea-h2">Tradie's comment</h2>
          <p class="mb-0">{{ sheet.submitted.comment }}</p>
        </section>

        <section v-if="sheet.photos && sheet.photos.length" class="ea-card">
          <h2 class="ea-h2">Photos</h2>
          <div class="ea-photos">
            <a
              v-for="photo in sheet.photos"
              :key="photo.id"
              :href="photo.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img :src="photo.url" alt="Timesheet photo" />
            </a>
          </div>
        </section>

        <section class="ea-card ea-export" data-test="export">
          <div class="ea-export-icon" aria-hidden="true">
            <b-icon-download />
          </div>
          <div class="ea-export-text">
            <strong>Download this timesheet</strong>
            <span class="ea-muted">
              The hours above, to keep for your records.
            </span>
          </div>
          <div class="ea-export-actions">
            <b-button
              variant="outline-secondary"
              size="sm"
              data-test="export-csv"
              @click="exportSheet('csv')"
            >
              <b-icon-file-earmark-spreadsheet class="mr-1" />
              Spreadsheet (CSV)
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              data-test="export-pdf"
              @click="exportSheet('pdf')"
            >
              <b-icon-file-earmark-pdf class="mr-1" />
              PDF
            </b-button>
          </div>
        </section>

        <!-- The decision -->
        <section v-if="canEdit" class="ea-card ea-signoff" data-test="decision">
          <div class="ea-signoff-head">
            <div class="ea-export-icon" aria-hidden="true">
              <b-icon-pen />
            </div>
            <div>
              <h2 class="ea-h2 mb-0">Sign off</h2>
              <span class="ea-muted">
                Add your name, then approve or reject this week.
              </span>
            </div>
          </div>

          <div class="ea-signoff-who">
            <b-form-group label="Your full name">
              <b-form-input v-model="form.name" data-test="name" />
            </b-form-group>
            <b-form-group label="Company (optional)">
              <b-form-input v-model="form.company" />
            </b-form-group>
            <b-form-group label="Position (optional)">
              <b-form-input v-model="form.position" />
            </b-form-group>
          </div>

          <b-form-group
            :label="
              hasChanges
                ? 'Comment explaining your changes (required)'
                : 'Comment (optional)'
            "
          >
            <b-form-textarea
              v-model="form.comment"
              rows="3"
              maxlength="500"
              data-test="comment"
            />
          </b-form-group>

          <div class="ea-declaration">
            <b-form-checkbox v-model="form.declaration" data-test="declaration">
              {{ declarationText }}
            </b-form-checkbox>
          </div>

          <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

          <div v-if="!rejecting" class="ea-signoff-actions">
            <b-button
              variant="outline-danger"
              :disabled="busy"
              data-test="reject-start"
              @click="rejecting = true"
            >
              Reject
            </b-button>
            <b-button
              variant="success"
              class="ea-approve"
              :disabled="busy || !canApprove"
              data-test="approve"
              @click="approve"
            >
              <b-icon-check2 class="mr-1" />
              {{ hasChanges ? 'Approve with changes' : 'Approve timesheet' }}
            </b-button>
          </div>
          <div v-else data-test="reject-form">
            <b-form-group label="Reason for rejecting (required)">
              <b-form-input
                v-model="form.rejectionReason"
                maxlength="255"
                placeholder="e.g. Wrong week, wrong person"
                data-test="reject-reason"
              />
            </b-form-group>
            <b-button
              variant="danger"
              block
              :disabled="
                busy || !form.rejectionReason.trim() || !form.name.trim()
              "
              data-test="reject-confirm"
              @click="reject"
            >
              Reject timesheet
            </b-button>
            <b-button
              variant="link"
              block
              :disabled="busy"
              @click="rejecting = false"
            >
              Back
            </b-button>
          </div>
          <p
            v-if="!canApprove && !rejecting"
            class="ea-muted mt-2 mb-0 ea-signoff-why"
            data-test="why-disabled"
          >
            {{ approveBlocker }}
          </p>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import externalApprovalApi, {
  dashboardSheetApi,
} from '@/utils/externalApprovalApi'
import {
  clearDashboardSession,
  clearPendingCode,
  readDashboardSession,
  readPendingCode,
  saveDashboardSession,
  savePendingCode,
} from '@/utils/externalSupervisorSession'
import {
  DECLARATION,
  buildExternalExportRows,
  dayChanged,
  dayError,
  describeApiError,
  formatDifference,
  formatMinutes,
  plantChanged,
  plantError,
  totalMinutes,
  workedMinutes,
} from '@/utils/externalApproval'

import { downloadRowsAsCsv, downloadRowsAsPdf } from '@/utils/pdfExport'

const LEAVE_LABELS = {
  annual_leave: 'Annual leave',
  sick_leave: 'Sick leave',
  personal_leave: 'Personal leave',
  public_holiday: 'Public holiday',
}

const clone = (v) => JSON.parse(JSON.stringify(v))

export default {
  name: 'ExternalApprovalPage',

  props: {
    // Injectable for tests; the route passes the token via :token.
    api: { type: Object, default: () => externalApprovalApi },
    // Set when the supervisor opened this sheet from their dashboard: the id
    // stands where the link token does, and the dashboard's session is used.
    dashboardId: { type: [String, Number], default: null },
    // Injectable for tests, like `api`: the client used in dashboard mode.
    dashboardApi: { type: Object, default: () => dashboardSheetApi },
  },

  data() {
    return {
      step: 'loading',
      link: {},
      blockedTitle: '',
      blockedMessage: '',
      codeSentTo: '',
      code: '',
      session: null,
      sheet: null,
      days: [],
      original: [],
      plant: [],
      originalPlant: [],
      rejecting: false,
      busy: false,
      error: null,
      form: {
        name: '',
        company: '',
        position: '',
        comment: '',
        declaration: false,
        rejectionReason: '',
      },
    }
  },

  computed: {
    token() {
      return this.dashboardId
        ? String(this.dashboardId)
        : this.$route.params.token
    },
    client() {
      return this.dashboardId ? this.dashboardApi : this.api
    },
    header() {
      return this.sheet || this.link || {}
    },
    weekLabel() {
      const h = this.header
      if (!h.week_start) return ''
      return `${dayjs(h.week_start).format('D MMM')} – ${dayjs(
        h.week_end,
      ).format('D MMM YYYY')}`
    },
    isDecided() {
      return !!(
        this.sheet &&
        this.sheet.state === 'decided' &&
        this.sheet.decision
      )
    },
    canEdit() {
      return !!this.sheet && this.sheet.state === 'awaiting_approval'
    },
    decisionTitle() {
      const d =
        this.sheet && this.sheet.decision && this.sheet.decision.decision
      return (
        {
          approved: 'Approved',
          amended: 'Approved with changes',
          rejected: 'Rejected',
        }[d] || 'Decided'
      )
    },
    decisionVariant() {
      const d =
        this.sheet && this.sheet.decision && this.sheet.decision.decision
      return d === 'rejected' ? 'danger' : 'success'
    },
    dayErrors() {
      return this.days.map((d) => (d.leave_type ? null : dayError(d)))
    },
    plantErrors() {
      return this.plant.map((p) => plantError(p))
    },
    hasChanges() {
      return (
        this.days.some((d, i) => dayChanged(this.original[i], d)) ||
        this.plant.some((p, i) => plantChanged(this.originalPlant[i], p))
      )
    },
    currentTotal() {
      return totalMinutes(this.days)
    },
    originalTotal() {
      return totalMinutes(this.original)
    },
    totalDifference() {
      return this.currentTotal - this.originalTotal
    },
    declarationText() {
      return DECLARATION(this.form.name.trim() || '[Supervisor Full Name]')
    },
    // Why Approve is disabled, so the supervisor is never left guessing.
    approveBlocker() {
      if (!this.form.name.trim()) return 'Enter your full name to continue.'
      if (this.dayErrors.some(Boolean) || this.plantErrors.some(Boolean)) {
        return 'Fix the highlighted hours first.'
      }
      if (this.hasChanges && !this.form.comment.trim()) {
        return 'Add a comment explaining your changes.'
      }
      if (!this.form.declaration) return 'Tick the declaration to approve.'
      return ''
    },
    canApprove() {
      return !this.approveBlocker
    },
  },

  created() {
    this.load()
  },

  methods: {
    // This sheet as the supervisor sees it: their changed hours once decided
    // with changes, else the hours sent.
    exportSheet(format) {
      const s = this.sheet
      const rows = buildExternalExportRows([
        {
          project_name: s.project_name,
          tradie_name: s.tradie_name,
          week_start: s.week_start,
          decision: s.decision && s.decision.decision,
          days: (s.decided || s.submitted).days,
        },
      ])
      const filename = `Timesheet ${s.tradie_name || ''} ${dayjs(
        s.week_start,
      ).format('DD MM YYYY')}`.replace(/\s+/g, ' ')
      if (format === 'pdf') downloadRowsAsPdf(filename, rows)
      else downloadRowsAsCsv(filename, rows)
    },

    formatMinutes,
    formatDifference,
    worked: workedMinutes,

    async load() {
      // Opened from the dashboard: its session, no emailed link or code here.
      if (this.dashboardId) {
        const saved = readDashboardSession()
        if (!saved) return this.toSignIn()
        this.session = saved.session
        await this.loadSheet()
        return
      }
      try {
        this.link = await this.client.show(this.token)
      } catch (e) {
        return this.block(e)
      }
      if (['expired', 'cancelled'].includes(this.link.state)) {
        this.blockedTitle =
          this.link.state === 'expired'
            ? 'This link has expired'
            : 'This link was cancelled'
        this.blockedMessage =
          this.link.state === 'expired'
            ? 'Ask the tradie to send the timesheet again to get a new link.'
            : 'The tradie changed or withdrew this timesheet. Use the most recent email you were sent.'
        this.step = 'blocked'
        return
      }
      // A session from earlier in this tab skips the code, e.g. after a refresh.
      const saved = this.readSession()
      if (saved) {
        this.session = saved
        if (await this.loadSheet()) return
      }
      // Already signed in as this email (a code entered on another link or the
      // dashboard)? Open this one with that session, no new code.
      if (await this.openWithDashboardSession()) return
      // A code was already emailed for this link and the tab was reloaded while
      // they read it: go back to the code box, don't send another code.
      const pending = readPendingCode(this.token)
      if (pending) {
        this.codeSentTo = pending.sentTo || this.link.code_sent_to || ''
        this.step = 'enter-code'
        return
      }
      this.step = 'send-code'
    },

    // True when the sheet is on screen. Any refusal (a different email, an
    // ended session) falls back to the emailed code.
    async openWithDashboardSession() {
      if (typeof this.client.exchangeSession !== 'function') return false
      const held = readDashboardSession()
      if (!held) return false
      try {
        const res = await this.client.exchangeSession(this.token, held.session)
        this.session = res.session
        this.saveSession(res.session)
        return await this.loadSheet()
      } catch (e) {
        this.session = null
        return false
      }
    },

    block(e) {
      const status = e && e.response && e.response.status
      this.blockedTitle =
        status === 410
          ? 'This link is no longer active'
          : 'This link is not valid'
      this.blockedMessage = describeApiError(
        e,
        'Use the most recent email you were sent, or ask the tradie to send it again.',
      )
      this.step = 'blocked'
    },

    async sendCode() {
      this.busy = true
      this.error = null
      try {
        const res = await this.client.sendCode(this.token)
        this.codeSentTo = res.sent_to
        savePendingCode(this.token, res.sent_to, res.code_expires_at)
        this.code = ''
        this.step = 'enter-code'
      } catch (e) {
        if (this.isGone(e)) return this.block(e)
        this.error = describeApiError(e, 'We could not send a code. Try again.')
      } finally {
        this.busy = false
      }
    },

    async verifyCode() {
      if (!this.code.trim()) return
      this.busy = true
      this.error = null
      try {
        const res = await this.client.verify(this.token, this.code.trim())
        this.session = res.session
        this.saveSession(res.session)
        clearPendingCode(this.token)
        // The same code also opens the dashboard for this email.
        if (res.dashboard_session) {
          saveDashboardSession(
            res.email,
            res.dashboard_session,
            res.session_expires_at,
          )
        }
        await this.loadSheet()
      } catch (e) {
        if (this.isGone(e)) return this.block(e)
        const left =
          e.response && e.response.data && e.response.data.attempts_remaining
        this.error =
          describeApiError(e, 'That code is not right.') +
          (left ? ` ${left} attempt${left === 1 ? '' : 's'} left.` : '')
        if (left === 0) {
          clearPendingCode(this.token)
          this.step = 'send-code'
        }
      } finally {
        this.busy = false
      }
    },

    // Returns true when the sheet is on screen.
    async loadSheet() {
      try {
        const sheet = await this.client.sheet(this.token, this.session)
        this.applySheet(sheet)
        this.step = 'sheet'
        return true
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.clearSession()
          this.toSignIn()
          return false
        }
        if (this.isGone(e)) {
          this.block(e)
          return false
        }
        this.error = describeApiError(e, 'We could not load the timesheet.')
        this.step = 'send-code'
        return false
      }
    },

    applySheet(sheet) {
      this.sheet = sheet
      // Show what was approved once decided, otherwise what was submitted.
      const shown = sheet.decided || sheet.submitted
      this.days = clone(shown.days)
      this.original = clone(sheet.submitted.days)
      this.plant = clone(shown.plant)
      this.originalPlant = clone(sheet.submitted.plant)
      if (!this.form.name)
        this.form.name = (sheet.supervisor && sheet.supervisor.name) || ''
      if (!this.form.company)
        this.form.company = (sheet.supervisor && sheet.supervisor.company) || ''
      if (!this.form.position)
        this.form.position =
          (sheet.supervisor && sheet.supervisor.position) || ''
    },

    dayIsChanged(i) {
      return !!this.original[i] && dayChanged(this.original[i], this.days[i])
    },
    plantIsChanged(i) {
      return (
        !!this.originalPlant[i] &&
        plantChanged(this.originalPlant[i], this.plant[i])
      )
    },
    amendedByEarlierVersion(i) {
      return this.isDecided && !!this.days[i] && !!this.days[i].original
    },

    // "Was 07:00–16:00", but a day that had nothing reads "No hours recorded",
    // not "Was no hours".
    wasText(day) {
      return day && day.start && day.finish
        ? `Was ${this.describeDay(day)}`
        : 'No hours recorded'
    },
    describeDay(day) {
      if (!day || !day.start || !day.finish) return 'no hours recorded'
      const brk = Number(day.break_minutes) || 0
      return `${day.start}–${day.finish}${brk ? `, ${brk}m break` : ''}`
    },
    describeOriginal(orig) {
      return this.describeDay(orig)
    },
    leaveLabel(type) {
      return LEAVE_LABELS[type] || String(type).replace(/_/g, ' ')
    },
    formatDay(date) {
      return dayjs(date).format('ddd D MMM')
    },
    formatDateTime(value) {
      return dayjs(value).format('D MMM YYYY, h:mm a')
    },

    payloadBase(action) {
      return {
        action,
        supervisor_name: this.form.name.trim(),
        supervisor_company: this.form.company.trim() || null,
        supervisor_position: this.form.position.trim() || null,
        comment: this.form.comment.trim() || null,
      }
    },

    async approve() {
      if (!this.canApprove) return
      const withChanges = this.hasChanges
      const body = {
        ...this.payloadBase(withChanges ? 'approve_with_changes' : 'approve'),
        declaration: true,
      }
      if (withChanges) {
        body.days = this.days
          .filter((d) => !d.leave_type)
          .map((d) => ({
            date: d.date,
            start: d.start || null,
            finish: d.finish || null,
            break_minutes: Number(d.break_minutes) || 0,
          }))
        body.plant = this.plant.map((p) => ({
          date: p.date,
          plant_equipment_id: p.plant_equipment_id,
          quantity: Number(p.quantity),
        }))
      }
      await this.submitDecision(body)
    },

    async reject() {
      await this.submitDecision({
        ...this.payloadBase('reject'),
        rejection_reason: this.form.rejectionReason.trim(),
      })
    },

    async submitDecision(body) {
      this.busy = true
      this.error = null
      try {
        await this.client.decide(this.token, this.session, body)
        this.rejecting = false
        await this.loadSheet()
        if (typeof window !== 'undefined') window.scrollTo(0, 0)
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.clearSession()
          this.toSignIn()
        } else if (e.response && [409, 410].includes(e.response.status)) {
          // Already decided elsewhere, or no longer open: show the real state.
          await this.loadSheet()
          this.error = describeApiError(
            e,
            'This timesheet can no longer be changed.',
          )
        } else {
          this.error = describeApiError(
            e,
            'We could not save your decision. Try again.',
          )
        }
      } finally {
        this.busy = false
      }
    },

    // Back to asking for a code: this link's own step, or the dashboard's sign-in.
    toSignIn() {
      if (this.dashboardId) {
        clearDashboardSession()
        this.$router.replace({ name: 'external-supervisor' }).catch(() => {})
      } else {
        this.step = 'send-code'
      }
    },

    isGone(e) {
      const s = e && e.response && e.response.status
      return s === 404 || s === 410
    },

    // sessionStorage can throw (private mode, blocked storage); the page works
    // without it, the supervisor just re-enters a code after a refresh.
    sessionKey() {
      return 'external-approval-session'
    },
    readSession() {
      try {
        const raw = window.sessionStorage.getItem(this.sessionKey())
        const saved = raw && JSON.parse(raw)
        return saved && saved.token === this.token ? saved.session : null
      } catch (e) {
        return null
      }
    },
    saveSession(session) {
      try {
        window.sessionStorage.setItem(
          this.sessionKey(),
          JSON.stringify({ token: this.token, session }),
        )
      } catch (e) {}
    },
    clearSession() {
      this.session = null
      try {
        window.sessionStorage.removeItem(this.sessionKey())
      } catch (e) {}
    },
  },
}
</script>

<style lang="scss" scoped>
.ea-page {
  min-height: 100vh;
  background: #f4f6f8;
  color: #1f2933;
}
.ea-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-bottom: 1px solid #e4e7eb;
}
.ea-logo {
  max-height: 48px;
  max-width: 120px;
  object-fit: contain;
}
.ea-back {
  display: inline-block;
  margin-bottom: 4px;
  font-size: 0.85rem;
}
.ea-company {
  font-size: 0.85rem;
  color: #616e7c;
}
.ea-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}
.ea-sub {
  font-size: 0.9rem;
  color: #616e7c;
}
.ea-body {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}
.ea-card {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}
.ea-h2 {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 12px;
}
.ea-code-input {
  font-size: 1.4rem;
  letter-spacing: 0.3em;
  text-align: center;
}
.ea-muted {
  color: #616e7c;
  font-size: 0.85rem;
}
.ea-day {
  padding: 12px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  margin-bottom: 10px;
  &.ea-changed {
    border-color: #f0b429;
    background: #fffbea;
  }
  &.ea-invalid {
    border-color: #e12d39;
  }
}
.ea-day-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}
.ea-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  label {
    margin: 0;
    span {
      display: block;
      font-size: 0.75rem;
      color: #616e7c;
    }
  }
}
.ea-signoff-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
// Name, company and position on one line where there's room.
.ea-signoff-who {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr;
  gap: 0 12px;
}
// The declaration is what the supervisor is signing, so it gets its own box.
.ea-declaration {
  padding: 12px 14px;
  margin-bottom: 16px;
  border: 1px solid #c7d7fe;
  border-radius: 10px;
  background: #f5f8ff;
}
.ea-signoff-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  .ea-approve {
    min-width: 220px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
.ea-signoff-why {
  text-align: right;
}
@media (max-width: 575px) {
  .ea-signoff-who {
    grid-template-columns: 1fr;
  }
  // Approve first and full width on a phone.
  .ea-signoff-actions {
    flex-direction: column-reverse;
    .btn {
      width: 100%;
    }
  }
  .ea-signoff-why {
    text-align: left;
  }
}
// A quiet row: it's for keeping a copy, not the main job of the page.
.ea-export {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}
.ea-export-icon {
  flex: 0 0 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #eef4ff;
  color: #2563eb;
}
.ea-export-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.ea-export-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }
}
@media (max-width: 575px) {
  .ea-export {
    flex-wrap: wrap;
  }
  .ea-export-actions {
    width: 100%;
    .btn {
      flex: 1;
    }
  }
}
.ea-days-head {
  display: none;
}
// Wider screens: one row per day (Day, Start, Finish, Break, Hours) with the
// column labels shown once, so a whole week fits without scrolling. Phones
// keep the stacked day cards above.
@media (min-width: 576px) {
  .ea-days {
    border: 1px solid #e4e7eb;
    border-radius: 10px;
    overflow: hidden;
  }
  .ea-days-head,
  .ea-day {
    display: grid;
    grid-template-columns: 110px 1fr 1fr 100px 96px;
    column-gap: 10px;
    align-items: center;
  }
  .ea-days-head {
    padding: 8px 12px;
    background: #f8f9fb;
    border-bottom: 1px solid #e4e7eb;
    font-size: 0.75rem;
    color: #616e7c;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .ea-day {
    margin: 0;
    padding: 8px 12px;
    border: 0;
    border-left: 3px solid transparent;
    border-bottom: 1px solid #eef0f2;
    border-radius: 0;
    &:last-child {
      border-bottom: 0;
    }
    &.ea-changed {
      border-left-color: #f0b429;
    }
    &.ea-invalid {
      border-left-color: #e12d39;
    }
  }
  .ea-day-head,
  .ea-fields {
    display: contents;
  }
  .ea-day-head strong {
    grid-column: 1;
    grid-row: 1;
  }
  .ea-worked {
    grid-column: 5;
    grid-row: 1;
    text-align: right;
    font-weight: 600;
    white-space: nowrap;
  }
  .ea-fields label {
    grid-row: 1;
    &:nth-child(1) {
      grid-column: 2;
    }
    &:nth-child(2) {
      grid-column: 3;
    }
    &:nth-child(3) {
      grid-column: 4;
    }
    // The column heading names the field; keep the label for screen readers.
    span {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
  }
  .ea-day > .ea-muted {
    grid-column: 2 / 5;
    grid-row: 1;
  }
  .ea-day > .ea-was,
  .ea-day > .ea-error {
    grid-column: 2 / -1;
    margin-top: 2px;
  }
}
.ea-was {
  margin-top: 6px;
  font-size: 0.85rem;
  color: #8d5b00;
}
.ea-error {
  margin-top: 6px;
  font-size: 0.85rem;
  color: #e12d39;
}
.ea-total {
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
  font-size: 1.05rem;
}
.ea-diff {
  margin-left: 6px;
  font-size: 0.85rem;
  color: #8d5b00;
}
.ea-plant {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  margin-bottom: 8px;
  &.ea-changed {
    border-color: #f0b429;
    background: #fffbea;
  }
}
.ea-qty {
  width: 96px;
}
.ea-photos {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 8px;
  }
}
@media (max-width: 480px) {
  .ea-fields {
    grid-template-columns: 1fr 1fr;
    label:last-child {
      grid-column: span 2;
    }
  }
}
</style>
